import { useCallback, useEffect, useState } from "react";
import vitalsTrackingService from "@/services/vitals-tracking.service";
import { PrescriptionVitalSuggestion } from "@/types/vitals";

/**
 * Vitals extracted from a member's prescriptions that are still awaiting an
 * add/ignore decision.
 *
 * Shared by the vitals landing screen and the readings detail page. The landing
 * screen matters most: a member with nothing tracked yet cannot reach the detail
 * page without first adding a vital by hand, so that is the only place they
 * would ever see the suggestion.
 *
 * Acting on a suggestion records the reading server-side (auto-enabling the
 * parameter if the member wasn't tracking it), so callers only need to refresh
 * their own view afterwards.
 */
export function usePrescriptionVitals(
  memberId: string,
  options: { enabled?: boolean } = {}
) {
  const { enabled = true } = options;
  const [suggestions, setSuggestions] = useState<PrescriptionVitalSuggestion[]>(
    []
  );
  const [isActioning, setIsActioning] = useState(false);

  const fetchSuggestions = useCallback(async (): Promise<
    PrescriptionVitalSuggestion[]
  > => {
    if (!memberId || !enabled) return [];
    try {
      return await vitalsTrackingService.getPrescriptionSuggestions(memberId);
    } catch {
      // A suggestion is an optional prompt — failing to load one is not worth
      // interrupting the screen it sits on.
      return [];
    }
  }, [memberId, enabled]);

  useEffect(() => {
    let cancelled = false;
    fetchSuggestions().then((next) => {
      if (!cancelled) setSuggestions(next);
    });
    return () => {
      cancelled = true;
    };
  }, [fetchSuggestions]);

  /** Applies the member's decision, then re-reads what is still pending. */
  const actOn = useCallback(
    async (
      suggestion: PrescriptionVitalSuggestion,
      action: "add" | "ignore"
    ) => {
      setIsActioning(true);
      try {
        await vitalsTrackingService.actionPrescriptionSuggestion(
          suggestion.prescriptionId,
          suggestion.parameter,
          action
        );
        setSuggestions(await fetchSuggestions());
      } finally {
        setIsActioning(false);
      }
    },
    [fetchSuggestions]
  );

  return { suggestions, isActioning, actOn };
}
